/**
 * ConnectionStatus Component
 * Displays the status of a bank connection with visual indicators
 */

import { cn } from '@/utils/cn';
import type { ConnectionStatus as ConnectionStatusType } from '@/types';

export interface ConnectionStatusProps {
  /**
   * Connection status
   */
  status: ConnectionStatusType;
  /**
   * Show status text alongside icon
   */
  showText?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Size of the status indicator
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Status configuration with colors and labels
 */
const statusConfig: Record<
  ConnectionStatusType,
  { label: string; color: string; bgColor: string; ringColor: string }
> = {
  active: {
    label: 'Connected',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    ringColor: 'ring-green-600/20',
  },
  pending: {
    label: 'Pending',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    ringColor: 'ring-yellow-600/20',
  },
  pending_reauth: {
    label: 'Needs Attention',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    ringColor: 'ring-orange-600/20',
  },
  error: {
    label: 'Error',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    ringColor: 'ring-red-600/20',
  },
  disconnected: {
    label: 'Disconnected',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    ringColor: 'ring-gray-600/20',
  },
};

/**
 * Size configuration
 */
const sizeConfig = {
  sm: {
    dot: 'h-1.5 w-1.5',
    text: 'text-xs',
    padding: 'px-2 py-0.5',
  },
  md: {
    dot: 'h-2 w-2',
    text: 'text-sm',
    padding: 'px-2.5 py-1',
  },
  lg: {
    dot: 'h-2.5 w-2.5',
    text: 'text-base',
    padding: 'px-3 py-1.5',
  },
};

/**
 * ConnectionStatus - Visual indicator for bank connection status
 *
 * @example
 * ```tsx
 * // Icon only
 * <ConnectionStatus status="active" />
 *
 * // With text
 * <ConnectionStatus status="error" showText />
 * ```
 */
export default function ConnectionStatus({
  status,
  showText = false,
  className,
  size = 'md',
}: ConnectionStatusProps) {
  const config = statusConfig[status];
  const sizeStyles = sizeConfig[size];

  if (!showText) {
    // Icon only version
    return (
      <span
        className={cn('relative inline-flex', className)}
        aria-label={config.label}
        role="status"
      >
        <span
          className={cn(
            'inline-block rounded-full',
            sizeStyles.dot,
            config.bgColor,
            status === 'active' && 'animate-pulse'
          )}
        />
        {status === 'active' && (
          <span
            className={cn(
              'absolute inline-flex h-full w-full rounded-full opacity-75',
              config.bgColor,
              'animate-ping'
            )}
            style={{ animationDuration: '2s' }}
          />
        )}
      </span>
    );
  }

  // Badge version with text
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium ring-1 ring-inset',
        sizeStyles.padding,
        sizeStyles.text,
        config.bgColor,
        config.color,
        config.ringColor,
        className
      )}
      role="status"
    >
      <span
        className={cn(
          'mr-1.5 inline-block rounded-full',
          sizeStyles.dot,
          status === 'active' ? 'bg-green-500' : '',
          status === 'pending' ? 'bg-yellow-500' : '',
          status === 'pending_reauth' ? 'bg-orange-500' : '',
          status === 'error' ? 'bg-red-500' : '',
          status === 'disconnected' ? 'bg-gray-500' : ''
        )}
      />
      {config.label}
    </span>
  );
}

/**
 * Get human-readable description for connection status
 */
export function getStatusDescription(status: ConnectionStatusType): string {
  switch (status) {
    case 'active':
      return 'Your bank account is connected and syncing normally.';
    case 'pending':
      return 'Connection is being set up. This usually takes a few moments.';
    case 'pending_reauth':
      return 'Your bank requires you to re-authenticate. Please reconnect your account.';
    case 'error':
      return 'There was a problem connecting to your bank. Please try reconnecting.';
    case 'disconnected':
      return 'This connection was disconnected. You revoked access at your bank.';
    default:
      return 'Unknown status.';
  }
}
